import { ApiError, type ThreadPriority } from '@central/api';
import { Card, PageHeader, charCount, color } from '@central/ui';
import { Alert, Button, FileInput, Select, TextInput, Textarea } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { api, deploymentsQuery, threadQuery } from '../api';
import { AttachFromDeployment } from '../components/AttachFromDeployment';
import { fileProblem, noAttach, uploadFiles, type AttachChoice } from '../components/attachments';
import { CcPicker } from '../components/PeopleNotified';
import { MAX_BODY } from '../components/messageRules';
import {
  newThreadKinds,
  threadAreas,
  urgencyOptions,
  type CustomerThreadKind,
} from '../components/threadLabels';
import { errorMessage } from '../format';

/** A new message to Rethink, prefilled from the search; sent, it opens its conversation page. */
export function NewMessagePage() {
  const search = useSearch({ from: '/app/messages/new' });
  return (
    <>
      <nav
        aria-label="Breadcrumb"
        style={{ display: 'flex', alignItems: 'center', fontSize: 14, minHeight: 40 }}
      >
        <Link
          to="/messages"
          activeOptions={{ exact: true }}
          style={{
            color: color.link,
            textDecoration: 'none',
            minHeight: 40,
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          <span aria-hidden>←&nbsp;</span>
          Messages & reports
        </Link>
      </nav>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <PageHeader
          title="New message"
          subtitle="A question, a renewal, a bug, an idea or a crash"
        />
        {/* Keyed so a different prefill (a compose link followed from this page) starts afresh. */}
        <NewMessageForm
          key={`${search.kind ?? ''}|${search.subject ?? ''}|${search.environmentId ?? ''}`}
          kind={search.kind}
          subject={search.subject}
          environmentId={search.environmentId}
        />
      </div>
    </>
  );
}

function NewMessageForm({
  kind,
  subject,
  environmentId,
}: {
  kind?: CustomerThreadKind;
  subject?: string;
  environmentId?: string;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const deployments = useQuery(deploymentsQuery);
  const blank = {
    kind: kind ?? 'question',
    priority: 'normal' as ThreadPriority,
    subject: subject ?? '',
    area: '',
    body: '',
    environmentId: environmentId ?? '',
  };
  const [form, setForm] = useState(blank);
  const [files, setFiles] = useState<File[]>([]);
  const [attach, setAttach] = useState<AttachChoice>(noAttach);
  const [cc, setCc] = useState<string[]>([]);
  const deployment = deployments.data?.items.find((d) => d.id === form.environmentId);
  const [problems, setProblems] = useState<{ subject?: string; body?: string; files?: string }>({});
  // A prefilled id counts only once it is one of the customer's deployments;
  // a stale or edited link would otherwise send an id the Select cannot show.
  const deploymentItems = deployments.data?.items;
  const deploymentId =
    form.environmentId &&
    (!deploymentItems || deploymentItems.some((d) => d.id === form.environmentId))
      ? form.environmentId
      : '';
  const create = useMutation({
    mutationFn: async () => {
      const attachments = files.length ? await uploadFiles(files) : undefined;
      const pull = deployment && (attach.logs || attach.health);
      return api.threads.create({
        kind: form.kind,
        priority: form.priority,
        subject: form.subject.trim(),
        area: form.area || undefined,
        body: form.body.trim(),
        environmentId: deploymentId || undefined,
        attachments,
        attach: pull
          ? {
              logs: attach.logs || undefined,
              services: attach.logs && attach.services.length ? attach.services : undefined,
              health: attach.health || undefined,
            }
          : undefined,
        cc: cc.length ? cc : undefined,
      });
    },
    onSuccess: async (d) => {
      qc.setQueryData(threadQuery(d.thread.id).queryKey, { pages: [d], pageParams: [undefined] });
      await qc.invalidateQueries({ queryKey: ['threads', 'list'] });
      await navigate({ to: '/messages/$threadId', params: { threadId: d.thread.id } });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const s = form.subject.trim();
    const b = form.body.trim();
    const p = {
      subject: !s
        ? 'Give it a title.'
        : charCount(s) > 200
          ? 'Keep the title under 200 characters.'
          : undefined,
      body: !b
        ? 'Describe it.'
        : charCount(b) > MAX_BODY
          ? 'Keep it under 20,000 characters.'
          : undefined,
      files: fileProblem(files, deployment ? Number(attach.logs) + Number(attach.health) : 0),
    };
    setProblems(p);
    if (!p.subject && !p.body && !p.files) create.mutate();
  };
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  return (
    <form onSubmit={submit} noValidate aria-label="New message">
      <Card style={{ maxWidth: 760 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
            <legend
              style={{ fontSize: 12, fontWeight: 600, color: color.textMuted, marginBottom: 6 }}
            >
              What is it about?
            </legend>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }} role="radiogroup">
              {newThreadKinds.map((k) => {
                const on = form.kind === k.value;
                return (
                  <label
                    key={k.value}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      minHeight: 40,
                      padding: '0 14px',
                      borderRadius: 8,
                      fontSize: 14,
                      cursor: 'pointer',
                      border: `1px solid ${on ? color.portalMark : color.borderStrong}`,
                      background: on ? color.portalNavActiveBg : color.surface,
                      color: on ? color.portalNavActiveText : color.text,
                      fontWeight: on ? 600 : 400,
                    }}
                  >
                    <input
                      type="radio"
                      name="kind"
                      value={k.value}
                      checked={on}
                      onChange={() => set('kind', k.value)}
                    />
                    {k.label}
                  </label>
                );
              })}
            </div>
          </fieldset>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 14,
            }}
          >
            <Select
              label="Deployment"
              placeholder="Not about one deployment"
              clearable
              data={(deployments.data?.items ?? []).map((d) => ({ value: d.id, label: d.name }))}
              value={deploymentId || null}
              error={
                create.error instanceof ApiError && create.error.fields?.environmentId
                  ? 'That deployment is no longer available. Choose another or clear it.'
                  : undefined
              }
              onChange={(v) => {
                set('environmentId', v ?? '');
                setAttach(noAttach);
              }}
            />
            <Select
              label="Area"
              placeholder="Choose an area"
              clearable
              data={threadAreas}
              value={form.area || null}
              onChange={(v) => set('area', v ?? '')}
            />
            <Select
              label="Urgency"
              data={urgencyOptions}
              value={form.priority}
              allowDeselect={false}
              onChange={(v) => v && set('priority', v as ThreadPriority)}
            />
          </div>
          <TextInput
            label="Title"
            value={form.subject}
            error={problems.subject}
            onChange={(e) => set('subject', e.currentTarget.value)}
          />
          <Textarea
            label="What happened, and what did you expect?"
            autosize
            minRows={4}
            value={form.body}
            error={problems.body}
            onChange={(e) => set('body', e.currentTarget.value)}
          />
          <AttachFromDeployment
            deployment={deployment}
            value={attach}
            onChange={setAttach}
            files={
              <FileInput
                label="Screenshots or files"
                placeholder="Choose files"
                multiple
                clearable
                value={files}
                error={problems.files}
                onChange={setFiles}
              />
            }
          />
          <CcPicker value={cc} onChange={setCc} error={create.error} />
          {create.error && <Alert color="red">{errorMessage(create.error)}</Alert>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button
              variant="default"
              renderRoot={(props) => (
                <Link {...props} to="/messages" activeOptions={{ exact: true }} />
              )}
            >
              Cancel
            </Button>
            <Button type="submit" loading={create.isPending}>
              Send to Rethink
            </Button>
          </div>
        </div>
      </Card>
    </form>
  );
}
